import type { Page } from '@core/api/types';
import type { EntityAdapter } from '@reduxjs/toolkit';
import { createSelector, lruMemoize } from '@reduxjs/toolkit';
import { isEmpty, isMatch } from 'lodash';
import type { ReduxEntities } from '../../enum';
import type { RootState } from '../../interface';
import type { SliceState } from '../interfaces';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const createSelectors = <T>(entity: ReduxEntities, adapter: EntityAdapter<T, string>) => {
  const selectSliceState = (state: RootState) => state[entity as keyof RootState] as SliceState<T>;
  const { selectEntities, selectAll, selectTotal } = adapter.getSelectors(selectSliceState);

  const selectById = (id?: string | null) =>
    id ? createSelector([selectEntities], (entities) => entities[id]) : () => undefined;

  const selectByIds = (ids: string[]) =>
    createSelector([selectEntities], (entities) =>
      ids.reduce(
        (acc, id) => (entities[id] === undefined ? acc : [...acc, entities[id] as T]),
        [] as T[],
      ),
    );

  const selectWithFilter = (filterFn: (entity: T) => boolean) =>
    createSelector([selectAll], (entityArray) => entityArray.filter(filterFn));

  const findFirst = (findFn: (entity: T) => boolean) =>
    createSelector([selectAll], (entityArray) => entityArray.find(findFn));

  const selectByProps = (props: Partial<T> = {}) =>
    isEmpty(props) ? () => [] as T[] : selectWithFilter((e: T) => isMatch(e as never, props));

  /**
   * This selector is a performance optimization. It will only recompute if the props change.
   *
   * NOTE: Because this is a factory, it returns a new selector reference on every call.
   * To prevent redundant re-renders, ALWAYS declare the returned selector at the module level
   * (outside components) or memoize it using React's `useMemo`.
   */
  const selectAllOnPropChange = (...props: (keyof T)[]) =>
    createSelector([selectAll], (x: T[]): T[] => x, {
      memoize: lruMemoize,
      memoizeOptions: {
        resultEqualityCheck: (a: T[], b: T[]): boolean =>
          a.length === b.length && a.every((e1, i) => props.every((p) => e1[p] === b[i][p])),
      },
      devModeChecks: { identityFunctionCheck: 'never' },
    });

  const selectPage = (state: RootState): Page | undefined => selectSliceState(state).page;

  return {
    findFirst,
    selectEntities,
    selectAll,
    selectAllOnPropChange,
    selectTotal,
    selectById,
    selectByIds,
    selectByProps,
    selectWithFilter,
    selectPage,
  };
};
